import { useRef } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { Row, Col, FloatingLabel, Form, Button as BSButton } from "react-bootstrap";
import { FrequencyTypeEnum, SubscriptionModel } from "../../data/entities";
import { usePageActions } from "../PageLayout";
import { SubscriptionService } from "../../services";
import { useUrlParams,
  useAsync,
  useCRUDHandlers,
  useSetPageTitle,
} from "../../hooks";
import { Button,
  Card,
  HandleDropDownListChange,
  IconAndText,
  IconDefinitions,
  SearchableFrequencyTypeDropdown,
} from "../../components";
import { formatDate } from "../../utils";

interface SubscriptionDetailsPageProps {
}

export function SubscriptionDetailsPage(props: Readonly<SubscriptionDetailsPageProps>) {

  const subscriptionId = parseInt(useUrlParams("id"));
  const subscriptionService = new SubscriptionService();
  const {value: subscription} = useAsync(() => subscriptionService.get(subscriptionId), [subscriptionId]);
  const { editItem } = useCRUDHandlers<SubscriptionModel>(subscriptionService, "Subscription");
  const submitRef = useRef<HTMLButtonElement>(null);

  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    frequency: yup.string()
      .oneOf(Object.values(FrequencyTypeEnum))
      .required("Frequency Type is required"),
    organizationName: yup.string().notRequired(),
    startDate: yup.date().required("Date is required"),
    price: yup.number().required("Price is required")
  });


  useSetPageTitle("Details", [subscriptionId]);
  usePageActions(
    <Button onClick={() => submitRef.current?.click()}>
      <IconAndText iconName={IconDefinitions.save} text="Save" />
    </Button>
  );

  if(subscription === null || subscription === undefined) return null;

  return (
    <Card title="Details">
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          name: subscription.name,
          frequency: subscription.frequency,
          organizationName: subscription?.organization?.name ?? "",
          startDate: subscription.startDate,
          price: subscription.price,
        }}
        onSubmit={(values) => {
          editItem({
            ...subscription,
            ...values,
            startDate: subscription.startDate
          })
        }}
      >
        {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col>
                <Form.Group controlId="name">
                  <FloatingLabel label="Name" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      isInvalid={touched.name && !!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group controlId="organizationName">
                  <FloatingLabel label="Organization Name" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Organization"
                      name="organizationName"
                      value={values.organizationName}
                      disabled={true}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <hr className="mt-0"/>

            <Row>
              <Col>
                <Form.Group controlId="startDate">
                  <FloatingLabel label="Start Date" className="mb-3">
                    <Form.Control
                      name="startDate"
                      placeholder="Start Date"
                      value={formatDate(values.startDate.toString())}
                      disabled={true}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group controlId="frequency">
                    <SearchableFrequencyTypeDropdown
                      name="frequency"
                      value={values.frequency}
                      placeholder="Billing Frequency"
                      onChange={(e) => HandleDropDownListChange(e, setFieldValue)}
                      isInvalid={touched.frequency && !!errors.frequency}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.frequency}
                    </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group controlId="price">
                  <FloatingLabel label="Price" className="mb-3">
                    <Form.Control
                      type="number"
                      name="price"
                      placeholder="Price"
                      value={values.price}
                      onChange={handleChange}
                      isInvalid={touched.price && !!errors.price}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.price}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <BSButton ref={submitRef} type="submit" className="d-none" />
          </Form>
        )}
      </Formik>
    </Card>

  );
}

export default SubscriptionDetailsPage;

